import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FormGroup, Label, Input, Button, Row, Col, Card, CardHeader, CardBody, FormFeedback } from 'reactstrap';
import { OnChangeCallback } from '../../../../../util/UtilityTypes'
import { CoreStringValueBuilderPart, StringValueBuilderPartValueSource } from '../../../../../models/CoreModels';
import SearchableSelectInput from '../../../../misc/SearchableSelectInput';
import OrderableList from '../../../../misc/OrderableList';
import { useTranslation } from 'react-i18next';
import { FieldValidation, StringValueBuilderPartDefinitions, defaultStringValueBuilderPartDefinitions, StringValueBuilderPartDefinition } from './StringValueBuilderDefinitions';
interface StringValueBuilderInputProps {
    name: string
    parts: CoreStringValueBuilderPart[]
    disabled?: boolean
    onChange: OnChangeCallback<CoreStringValueBuilderPart[]>
    partDefinitions?: StringValueBuilderPartDefinitions
}

interface StringValueBuilderPartFormProps {
    name: string
    disabled?: boolean
    onAdd: (part: CoreStringValueBuilderPart) => void
    partDefinitions: StringValueBuilderPartDefinitions
}

const StringValueBuilderPartForm: React.FC<StringValueBuilderPartFormProps> = ({ name, disabled, onAdd,partDefinitions }) => {
    const [formPart, setFormPart] = useState<CoreStringValueBuilderPart>({ valueSource: StringValueBuilderPartValueSource.UNKNOWN });
    const [invalid, setInvalid] = useState<FieldValidation[]>([]);
    const { t } = useTranslation(['configuration', 'common'])

    useEffect(() => {
        setInvalid([]);
    }, [formPart.valueSource, formPart.valueText, formPart.minLength, formPart.maxLength])

    const saveNew = useCallback(() => {
        const validation = partDefinitions[formPart.valueSource]?.validate(formPart) ?? [{field: "valueSource", message: t('stringValueBuilder.partForm.mustBeSpecified', {ns: 'configuration'})}];

        if (validation.length !== 0) {
            setInvalid(validation)
            return;
        }

        onAdd(formPart);

        setFormPart({ valueSource: StringValueBuilderPartValueSource.UNKNOWN });
    }, [onAdd, formPart, setFormPart])


    const show = (field: string) => {
        return partDefinitions[formPart.valueSource]?.fields?.some(x => x === field) ?? false;
    }

    const isRequired = (field: string) => {
        return partDefinitions[formPart.valueSource]?.requiredFields?.some(x => x === field) ?? false;
    }

    return (
        <div>
            <Row>
                <Col xs="3">
                    <FormGroup>
                        <Label>{t('stringValueBuilder.partForm.valueSource.label', {ns: 'configuration'})}</Label>
                        <SearchableSelectInput
                            name={`${name}.valueSource`}
                            defaultText={t('stringValueBuilder.partForm.valueSource.defaultText', {ns: 'configuration'})}
                            selected={formPart.valueSource}
                            disabled={disabled}
                            onChange={(_, v) => {
                                if (v) {
                                    //reset all values on typeswitch
                                    setFormPart({ valueSource: v })
                                } else {
                                    setFormPart({ valueSource: StringValueBuilderPartValueSource.UNKNOWN })
                                }
                            }}
                            items={(Object.entries(partDefinitions) as [string, StringValueBuilderPartDefinition][]).map(([key, value]) => ({ value: parseInt(key, 10), label: t(value.description, {ns: 'configuration'}) }))}

                        />
                    </FormGroup>
                </Col>
                {show("valueText") &&
                    <Col xs="3">
                        <FormGroup>
                            <Label for={`${name}.valueText`}>{t('stringValueBuilder.partForm.valueText.label', {ns: 'configuration'})} {isRequired('valueText') ? '*' : ''}</Label>
                            <Input
                                type="text"
                                name={`${name}.valueText`}
                                id={`${name}.valueText`}
                                className="w-100"
                                value={formPart.valueText || ''}
                                disabled={disabled}
                                onChange={(e) => setFormPart({ ...formPart, valueText: e.target.value })}
                                invalid={invalid.some(x => x.field === "valueText")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "valueText")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                {show("minLength") &&
                    <Col xs="2">
                        <FormGroup>
                            <Label for={`${name}.minLength`}>{t('stringValueBuilder.partForm.minLength.label', {ns: 'configuration'})} {isRequired('minLength') ? '*' : ''}</Label>
                            <Input
                                type="number"
                                title={t('stringValueBuilder.partForm.minLength.title', {ns: 'configuration'})}
                                name={`${name}.minLength`}
                                className="w-100"
                                value={formPart.minLength === undefined ? '' : formPart.minLength}
                                disabled={disabled}
                                onChange={e => setFormPart({ ...formPart, minLength: isNaN(parseInt(e.target.value, 10)) ? undefined : parseInt(e.target.value, 10) })}
                                invalid={invalid.some(x => x.field === "minLength")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "minLength")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                {show("maxLength") &&
                    <Col xs="2">
                        <FormGroup>
                            <Label for={`${name}.maxLength`}>{t('stringValueBuilder.partForm.maxLength.label', {ns: 'configuration'})}{isRequired('maxLength') ? '*' : ''}</Label>
                            <Input
                                type="number"
                                title={t('stringValueBuilder.partForm.maxLength.title', {ns: 'configuration'})}
                                name={`${name}.maxLength`}
                                className="w-100"
                                value={formPart.maxLength === undefined ? '' : formPart.maxLength}
                                disabled={disabled}
                                onChange={e => setFormPart({ ...formPart, maxLength: isNaN(parseInt(e.target.value, 10)) ? undefined : parseInt(e.target.value, 10) })}
                                invalid={invalid.some(x => x.field === "maxLength")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "maxLength")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                <Col xs="2">
                    <Button style={{ marginTop: "2rem" }} onClick={saveNew} disabled={disabled || formPart.valueSource === StringValueBuilderPartValueSource.UNKNOWN || invalid.length !== 0}>{t('action.add', {ns: 'common'})}</Button>
                </Col>
            </Row>

        </div >
    );
}


const StringValueBuilderInput: React.FC<StringValueBuilderInputProps> = ({
    parts, onChange, disabled, name, partDefinitions}) => {
    const { t } = useTranslation(['configuration', 'common'])
    const resolvedPartDefinitions: StringValueBuilderPartDefinitions = partDefinitions ?? defaultStringValueBuilderPartDefinitions; 
    const onAdd = useCallback((part: CoreStringValueBuilderPart) => {
        onChange(name, [...parts, part]);
    }, [parts, name, onChange]);


    const renderItem = useCallback((part: CoreStringValueBuilderPart) => {
        return t(resolvedPartDefinitions[part.valueSource]?.render(part), {ns: 'configuration', MaxLength: part.maxLength, MinLength: part.minLength, ValueSource: part.valueSource, ValueText: part.valueText }) ?? t('stringValueBuilder.unknown', {ns: 'configuration'});
    }, [resolvedPartDefinitions, t]);

    const example = useMemo(() => {
        return parts.reduce((acc: string | undefined, part: CoreStringValueBuilderPart) => {
            var partStr = resolvedPartDefinitions[part.valueSource]?.example(part) ?? "";
            return acc + partStr;
        }, "");
    }, [parts, resolvedPartDefinitions])

    return (
        <Card className="mb-3">
            <CardHeader className="d-block">
                {t('stringValueBuilder.header', {ns: 'configuration'})}
            </CardHeader>
            <CardBody>
                {parts.length > 0 && <>
                    <OrderableList name={name} disabled={disabled} parts={parts} onChange={onChange} renderItem={renderItem} />
                    <div className="py-3">{t('stringValueBuilder.example', {ns: 'configuration'})} <span>{example}</span></div>
                </>}

                <StringValueBuilderPartForm name={name} disabled={disabled} onAdd={onAdd} partDefinitions={resolvedPartDefinitions} />
            </CardBody>
        </Card>
    );
}

export default StringValueBuilderInput;
